import {
  ADJUSTMENTS,
  defaultAdjustmentState,
  type AdjustmentId,
  type AdjustmentState,
} from '../adjustments';
import { FILTERS, type FilterId } from '../filters';
import { BLACK, TRANSPARENT, WHITE } from '../core/color';
import {
  cloneDocumentMeta,
  createAdjustmentLayer,
  createDocumentMeta,
  createFillLayer,
  createGroupLayer,
  createLayer,
  createSmartLayer,
  duplicateLayerTree,
  nextLayerName,
} from '../core/document';
import type { LayerEffects } from '../core/layerStyles';
import { BUILTIN_GRADIENTS } from '../core/gradient';
import { allPatterns } from '../core/patterns';
import {
  applyLayerPatch,
  countLayers,
  flattenLayers,
  insertLayerAt,
  isGroup,
  isSurfaceLayer,
  findLayerById,
  locateLayer,
  mapLayer,
  moveLayerInTree,
  removeLayerFromTree,
  type InsertTarget,
  type LayerPatch,
} from '../core/layerTree';
import {
  clamp,
  mat3Scaling,
  mat3TransformPoint,
  mat3Translation,
  type Mat3,
} from '../core/math';
import type {
  AdjustmentLayer,
  BlendMode,
  DocumentMeta,
  ExportSettings,
  FillContent,
  FillLayer,
  Layer,
  Point,
  RGBA,
  Rect,
  SelectionCombineMode,
  SmartFilter,
  SurfaceLayer,
  ViewState,
} from '../core/types';
import { getEngine } from '../engine/engine';
import {
  bakeSmartLayer,
  captureSmartSource,
  makeSmartFilter,
  toRasterFromSmart,
  toSmartLayer,
} from '../engine/smartOps';
import { FloatingSelection } from '../engine/floatOps';
import {
  blankSnapshot,
  metaOp,
  selectionOp,
  surfaceOp,
  layerMaskOp,
  type EngineHistoryOp,
} from '../engine/historyOps';
import {
  applyAdjustment as applyAdjustmentOp,
  applyFilter as applyFilterOp,
  bucketFill,
  clearSelection as clearSelectionPixels,
  commitLayerTransform,
  commitLayerWarp,
  copyLayerPixels,
  copyPixels,
  drawPixelsIntoLayer,
  fillLayerSolid,
  fillSelection,
  flattenInto,
  mergeLayerDown,
  recanvasDocument,
  renderWarpPreview,
  resampleDocument,
  strokeSelection,
  transformSelectionMask,
  uploadImageToLayer,
} from '../engine/ops';
import { renderTextLayer } from '../engine/textOps';
import {
  composeSrcMatrices,
  contentOffsetSrcMatrix,
  fitZoom,
  MAX_ZOOM,
  MIN_ZOOM,
  nextZoomStep,
  srcUvPureTranslation,
  zoomAt,
} from '../gl/view';
import {
  getInternalClipboard,
  isOwnMirror,
  readImageFromSystemClipboard,
  setInternalClipboard,
  writeImageToSystemClipboard,
} from '../io/clipboard';
import { downloadBlob, EXPORT_EXTENSIONS, exportDocument } from '../io/export';
import { loadImage } from '../io/import';
import type { ShapeRaster } from '../selection/shapes';
import type { StrokeLocation } from '../selection/stroke';
import { geometryToSubpaths, transformSubpaths } from '../vector/geometry';
import { createGuide, type Guide } from '../core/guides';
import { EMPTY_TARGETS, snapTargets, type SnapTargets } from '../core/snap';
import type { WarpMesh } from '../core/warp';
import { clearLiveOverrides, liveRender, setLivePreview } from './renderState';
import { forgetDocument, scheduleAutosave } from './session';
import { useAppStore, type AppState } from './store';

const engine = getEngine();

function state(): AppState {
  return useAppStore.getState();
}

export function activeDocument(): DocumentMeta | null {
  const current = state();
  if (!current.activeDocumentId) return null;
  return current.documents.find((doc) => doc.id === current.activeDocumentId) ?? null;
}

/** The layer pixel operations may write to, or null with a reason on screen. */
function activeLayerId(doc: DocumentMeta): string | null {
  const layer = findLayerById(doc.layers, doc.activeLayerId);
  if (!layer) return null;
  if (!isSurfaceLayer(layer)) {
    state().setStatus(
      layer.kind === 'group'
        ? 'A group cannot be edited directly. Select a layer inside it.'
        : 'An adjustment layer has no pixels to edit.',
    );
    return null;
  }
  if (layer.kind === 'text') {
    state().setStatus('Rasterize the text layer before painting on it');
    return null;
  }
  if (layer.locked || layer.lockPixels) {
    state().setStatus(`The layer "${layer.name}" is locked`);
    return null;
  }
  return layer.id;
}

/** Where a new layer goes: directly above the active one, in the same group. */
export function insertAboveActive(doc: DocumentMeta): InsertTarget {
  const location = locateLayer(doc.layers, doc.activeLayerId ?? '');
  if (!location) return { parentId: null, index: doc.layers.length };
  // Adding while a collapsed group is selected puts the layer above the group.
  if (isGroup(location.layer) && location.layer.expanded) {
    return { parentId: location.layer.id, index: location.layer.children.length };
  }
  return { parentId: location.parent?.id ?? null, index: location.index + 1 };
}

/** Snapshots the pixels of a whole branch so removing it stays undoable. */
function removalOps(doc: DocumentMeta, root: Layer): EngineHistoryOp[] {
  const ops: EngineHistoryOp[] = [];
  for (const layer of flattenLayers([root])) {
    if (!isSurfaceLayer(layer)) continue;
    ops.push(surfaceOp(layer.id, engine.snapshotSurface(doc, layer.id), null));
    const mask = engine.snapshotLayerMask(doc, layer.id);
    if (mask) ops.push(layerMaskOp(layer.id, mask, null));
  }
  return ops;
}

function releaseBranch(docId: string, root: Layer): void {
  const resources = engine.peek(docId);
  for (const layer of flattenLayers([root])) resources?.deleteLayer(layer.id);
}

function syncHistory(docId: string): void {
  const resources = engine.peek(docId);
  if (!resources) return;
  const { history } = resources;
  state().setHistoryView(docId, {
    labels: history.steps.map((step) => step.label),
    position: history.position,
    canUndo: history.canUndo,
    canRedo: history.canRedo,
    bytes: history.bytes,
  });
}

export function syncSelectionFlag(docId: string): void {
  const resources = engine.peek(docId);
  if (!resources) return;
  const hasSelection = !resources.selection.isEmpty;
  state().replaceDocument(docId, (doc) =>
    doc.hasSelection === hasSelection ? doc : { ...doc, hasSelection },
  );
}

/** Records a completed change and marks the document dirty. */
export function commitHistory(docId: string, label: string, ops: EngineHistoryOp[] | null): void {
  if (!ops || ops.length === 0) return;
  const resources = engine.peek(docId);
  if (!resources) return;
  resources.history.push(label, ops);
  state().replaceDocument(docId, (doc) => (doc.dirty ? doc : { ...doc, dirty: true }));
  syncHistory(docId);
  scheduleAutosave(docId);
  engine.invalidate();
}

/**
 * Applies a metadata change and records it, optionally alongside pixel ops that
 * belong to the same user action.
 */
export function commitMeta(
  doc: DocumentMeta,
  label: string,
  next: DocumentMeta,
  pixelOps: EngineHistoryOp[] = [],
): void {
  const before = cloneDocumentMeta(doc);
  state().setDocument(next);
  commitHistory(doc.id, label, [...pixelOps, metaOp(before, cloneDocumentMeta(next))]);
}

let pendingMeta: { docId: string; meta: DocumentMeta } | null = null;

/** Call before a slider drag so the whole drag becomes one undo step. */
export function beginMetaTransaction(): void {
  const doc = activeDocument();
  if (!doc) return;
  pendingMeta = { docId: doc.id, meta: cloneDocumentMeta(doc) };
}

export function commitMetaTransaction(label: string): void {
  const pending = pendingMeta;
  pendingMeta = null;
  if (!pending) return;
  const doc = activeDocument();
  if (!doc || doc.id !== pending.docId) return;
  commitHistory(doc.id, label, [metaOp(pending.meta, cloneDocumentMeta(doc))]);
}

/** Updates a layer without touching history; pair with a meta transaction. */
export function patchLayerTransient(layerId: string, patch: LayerPatch): void {
  const doc = activeDocument();
  if (!doc) return;
  state().replaceDocument(doc.id, (current) => ({
    ...current,
    layers: mapLayer(current.layers, layerId, (layer) => applyLayerPatch(layer, patch)),
  }));
  engine.invalidate();
}

export function patchLayer(layerId: string, patch: LayerPatch, label: string): void {
  const doc = activeDocument();
  if (!doc) return;
  const next: DocumentMeta = {
    ...doc,
    layers: mapLayer(doc.layers, layerId, (layer) => applyLayerPatch(layer, patch)),
  };
  commitMeta(doc, label, next);
}

// --- Documents ---

export interface NewDocumentParams {
  name: string;
  width: number;
  height: number;
  background: 'white' | 'transparent' | 'color';
  color?: RGBA;
}

export function newDocument(params: NewDocumentParams): DocumentMeta | null {
  if (!engine.available) return null;
  const maxSize = engine.caps.maxTextureSize;
  const width = clamp(Math.round(params.width), 1, maxSize);
  const height = clamp(Math.round(params.height), 1, maxSize);

  const isTransparent = params.background === 'transparent';
  const layer = createLayer(isTransparent ? 'Layer 1' : 'Background');
  const doc = createDocumentMeta({ name: params.name, width, height, layers: [layer] });

  const view: ViewState = {
    zoom: fitZoom(engine.canvasSize, width, height),
    centerX: width / 2,
    centerY: height / 2,
    rotation: 0,
  };
  state().addDocument(doc, view);

  engine.resourcesFor(doc);
  const fill =
    params.background === 'white' ? WHITE : params.background === 'color' ? params.color! : TRANSPARENT;
  if (fill.a > 0) fillLayerSolid(engine, doc, layer.id, fill);

  syncHistory(doc.id);
  scheduleAutosave(doc.id);
  engine.invalidate();
  return doc;
}

export async function openPsdAsDocument(file: Blob): Promise<DocumentMeta | null> {
  if (!engine.available) return null;
  const { parsePsdFile } = await import('../io/psd');
  const buffer = await file.arrayBuffer();
  const name =
    file instanceof File ? file.name.replace(/\.(psd|psb)$/i, '') : 'Untitled';
  let parsed;
  try {
    parsed = parsePsdFile(buffer, name);
  } catch (error) {
    state().setStatus(
      `Could not open PSD: ${error instanceof Error ? error.message : 'unknown error'}`,
    );
    return null;
  }
  const { meta, surfaces, masks } = parsed;
  const maxSize = engine.caps.maxTextureSize;
  if (meta.width > maxSize || meta.height > maxSize) {
    state().setStatus(
      `The image ${meta.width}×${meta.height} exceeds the GPU limit (${maxSize} px)`,
    );
    return null;
  }
  const view: ViewState = {
    zoom: fitZoom(engine.canvasSize, meta.width, meta.height),
    centerX: meta.width / 2,
    centerY: meta.height / 2,
    rotation: 0,
  };
  state().addDocument(meta, view);
  const resources = engine.resourcesFor(meta);
  const rect = { x: 0, y: 0, width: meta.width, height: meta.height };
  for (const [layerId, pixels] of Object.entries(surfaces)) {
    resources.ensureLayer(layerId).surface.writePixels(rect, pixels);
    const layer = findLayerById(meta.layers, layerId);
    if (layer?.kind === 'smart') {
      resources.ensureSmartSource(layerId).texture.uploadPixels(rect, pixels);
    }
  }
  for (const [layerId, maskPixels] of Object.entries(masks)) {
    resources.ensureMask(layerId).texture.uploadPixels(rect, maskPixels);
  }
  // Rebuild text surfaces from editable metadata when Photoshop did not ship a raster.
  for (const layer of flattenLayers(meta.layers)) {
    if (layer.kind === 'text' && !surfaces[layer.id]) {
      renderTextLayer(engine, meta, layer, false);
    }
  }
  syncHistory(meta.id);
  scheduleAutosave(meta.id);
  engine.invalidate();
  state().setStatus(`Opened ${meta.width}×${meta.height} PSD`);
  return meta;
}

export async function saveDocumentAsPsd(): Promise<void> {
  const doc = activeDocument();
  if (!doc || !engine.available) return;
  const { documentToPsd, downloadPsd } = await import('../io/psd');
  try {
    const buffer = documentToPsd(engine, doc);
    downloadPsd(buffer, doc.name || 'Untitled');
  } catch (error) {
    state().setStatus(
      `Could not save PSD: ${error instanceof Error ? error.message : 'unknown error'}`,
    );
  }
}

export async function openPdfAsDocument(file: Blob): Promise<DocumentMeta | null> {
  if (!engine.available) return null;
  const { parsePdfFile } = await import('../io/pdf');
  const buffer = await file.arrayBuffer();
  const name =
    file instanceof File ? file.name.replace(/\.pdf$/i, '') : 'Untitled';
  let parsed;
  try {
    state().setStatus('Opening PDF…');
    parsed = await parsePdfFile(buffer, name);
  } catch (error) {
    state().setStatus(
      `Could not open PDF: ${error instanceof Error ? error.message : 'unknown error'}`,
    );
    return null;
  }
  const maxSize = engine.caps.maxTextureSize;
  if (parsed.width > maxSize || parsed.height > maxSize) {
    state().setStatus(
      `The PDF ${parsed.width}×${parsed.height} exceeds the GPU limit (${maxSize} px)`,
    );
    return null;
  }
  const layers = parsed.pages.map((page, index) =>
    createLayer(index === 0 && parsed.pages.length === 1 ? 'Background' : page.name),
  );
  const doc = createDocumentMeta({
    name: parsed.name,
    width: parsed.width,
    height: parsed.height,
    layers,
  });
  const view: ViewState = {
    zoom: fitZoom(engine.canvasSize, doc.width, doc.height),
    centerX: doc.width / 2,
    centerY: doc.height / 2,
    rotation: 0,
  };
  state().addDocument(doc, view);
  engine.resourcesFor(doc);
  for (let i = 0; i < parsed.pages.length; i += 1) {
    const page = parsed.pages[i]!;
    const layer = layers[i]!;
    const placement = {
      x: (doc.width - page.width) / 2,
      y: (doc.height - page.height) / 2,
      width: page.width,
      height: page.height,
    };
    uploadImageToLayer(engine, doc, layer.id, page.canvas, placement);
  }
  syncHistory(doc.id);
  scheduleAutosave(doc.id);
  engine.invalidate();
  state().setStatus(
    parsed.pages.length === 1
      ? `Opened ${doc.name}`
      : `Opened ${doc.name} (${parsed.pages.length} pages as layers)`,
  );
  return doc;
}

export async function saveDocumentAsPdf(): Promise<void> {
  const doc = activeDocument();
  if (!doc || !engine.available) return;
  const { documentToPdf, downloadPdf } = await import('../io/pdf');
  try {
    state().setStatus('Encoding PDF…');
    const buffer = await documentToPdf(engine, doc, { quality: 0.92, scale: 1 });
    downloadPdf(buffer, doc.name || 'Untitled');
    state().setStatus(`Saved ${doc.name || 'Untitled'}.pdf`);
    state().replaceDocument(doc.id, (current) => ({ ...current, dirty: false }));
  } catch (error) {
    state().setStatus(
      `Could not save PDF: ${error instanceof Error ? error.message : 'unknown error'}`,
    );
  }
}

export async function openImageAsDocument(file: Blob): Promise<DocumentMeta | null> {
  if (!engine.available) return null;
  const image = await loadImage(file);
  try {
    const maxSize = engine.caps.maxTextureSize;
    if (image.width > maxSize || image.height > maxSize) {
      state().setStatus(
        `The image ${image.width}×${image.height} exceeds the GPU limit (${maxSize} px)`,
      );
      return null;
    }

    const layer = createLayer('Background');
    const doc = createDocumentMeta({
      name: image.name,
      width: image.width,
      height: image.height,
      layers: [layer],
    });
    const view: ViewState = {
      zoom: fitZoom(engine.canvasSize, doc.width, doc.height),
      centerX: doc.width / 2,
      centerY: doc.height / 2,
      rotation: 0,
    };
    state().addDocument(doc, view);
    engine.resourcesFor(doc);
    uploadImageToLayer(engine, doc, layer.id, image.source);
    syncHistory(doc.id);
    scheduleAutosave(doc.id);
    engine.invalidate();
    return doc;
  } finally {
    image.release();
  }
}

export async function placeImageAsLayer(file: Blob): Promise<void> {
  const doc = activeDocument();
  if (!doc) {
    await openImageAsDocument(file);
    return;
  }
  const image = await loadImage(file);
  try {
    const layer = createLayer(image.name || nextLayerName(doc));
    // Fit the incoming image inside the canvas, centred, without upscaling.
    const scale = Math.min(1, doc.width / image.width, doc.height / image.height);
    const width = image.width * scale;
    const height = image.height * scale;
    const placement = {
      x: (doc.width - width) / 2,
      y: (doc.height - height) / 2,
      width,
      height,
    };

    const next: DocumentMeta = {
      ...doc,
      layers: [...doc.layers, layer],
      activeLayerId: layer.id,
    };
    state().setDocument(next);
    engine.resourcesFor(next);
    uploadImageToLayer(engine, next, layer.id, image.source, placement);

    commitHistory(doc.id, 'Place Image', [
      surfaceOp(layer.id, null, engine.snapshotSurface(next, layer.id)),
      metaOp(cloneDocumentMeta(doc), cloneDocumentMeta(next)),
    ]);
  } finally {
    image.release();
  }
}

export function closeDocument(docId: string): void {
  clearLiveOverrides();
  forgetDocument(docId);
  engine.release(docId);
  state().removeDocument(docId);
  engine.invalidate();
}

export function activateDocument(docId: string): void {
  clearLiveOverrides();
  state().setActiveDocument(docId);
  engine.invalidate();
}

// --- Layers ---

export function addLayer(): void {
  const doc = activeDocument();
  if (!doc) return;
  const layer = createLayer(nextLayerName(doc));
  const layers = insertLayerAt(doc.layers, insertAboveActive(doc), layer);
  const next: DocumentMeta = { ...doc, layers, activeLayerId: layer.id };

  state().setDocument(next);
  engine.resourcesFor(next);
  commitHistory(doc.id, 'New Layer', [
    surfaceOp(layer.id, null, blankSnapshot(doc.width, doc.height)),
    metaOp(cloneDocumentMeta(doc), cloneDocumentMeta(next)),
  ]);
}

/** Deletes a layer, or a group together with everything inside it. */
export function deleteLayer(layerId: string): void {
  const doc = activeDocument();
  if (!doc) return;
  const location = locateLayer(doc.layers, layerId);
  if (!location) return;
  if (countLayers(doc.layers) - countLayers([location.layer]) < 1) return;

  const ops = removalOps(doc, location.layer);
  const layers = removeLayerFromTree(doc.layers, layerId).layers;

  // Selection lands on the neighbour that took the deleted layer's place, or on
  // the parent group once it is left empty.
  const parent = location.parent ? findLayerById(layers, location.parent.id) : null;
  const pool = parent && isGroup(parent) ? parent.children : layers;
  const fallback =
    pool[Math.min(location.index, pool.length - 1)] ?? parent ?? layers[layers.length - 1];
  const next: DocumentMeta = { ...doc, layers, activeLayerId: fallback?.id ?? null };

  state().setDocument(next);
  releaseBranch(doc.id, location.layer);
  commitHistory(doc.id, 'Delete Layer', [
    ...ops,
    metaOp(cloneDocumentMeta(doc), cloneDocumentMeta(next)),
  ]);
}

export function duplicateLayer(layerId: string): void {
  const doc = activeDocument();
  if (!doc) return;
  const location = locateLayer(doc.layers, layerId);
  if (!location) return;

  const copy = duplicateLayerTree(location.layer);
  const renamed = applyLayerPatch(copy, { name: `${location.layer.name} copy` });
  const layers = insertLayerAt(
    doc.layers,
    { parentId: location.parent?.id ?? null, index: location.index + 1 },
    renamed,
  );
  const next: DocumentMeta = { ...doc, layers, activeLayerId: renamed.id };

  state().setDocument(next);
  engine.resourcesFor(next);

  // `duplicateLayerTree` keeps the order of the branch, so the flattened lists
  // line up one to one and give the surface pairs to copy.
  const sources = flattenLayers([location.layer]);
  const copies = flattenLayers([renamed]);
  const ops: EngineHistoryOp[] = [];
  for (let i = 0; i < sources.length; i += 1) {
    if (!isSurfaceLayer(sources[i])) continue;
    copyLayerPixels(engine, next, sources[i].id, copies[i].id);
    ops.push(surfaceOp(copies[i].id, null, engine.snapshotSurface(next, copies[i].id)));
    const maskAfter = engine.snapshotLayerMask(next, copies[i].id);
    if (maskAfter) ops.push(layerMaskOp(copies[i].id, null, maskAfter));
  }

  commitHistory(doc.id, 'Duplicate Layer', [
    ...ops,
    metaOp(cloneDocumentMeta(doc), cloneDocumentMeta(next)),
  ]);
}

/**
 * Ctrl+J — Layer Via Copy when there is a selection, otherwise Duplicate Layer.
 * Selected pixels stay in place on a new layer; the source layer is unchanged.
 */
export function duplicateLayerOrViaCopy(): void {
  const doc = activeDocument();
  if (!doc) return;

  const hasSelection = !engine.resourcesFor(doc).selection.isEmpty;
  if (!hasSelection) {
    if (doc.activeLayerId) duplicateLayer(doc.activeLayerId);
    return;
  }

  const layerId = activeLayerId(doc);
  if (!layerId) return;

  const copied = copyPixels(engine, doc, layerId);
  if (!copied) {
    state().setStatus('There is nothing to copy');
    return;
  }

  const layer = createLayer(nextLayerName(doc));
  const next: DocumentMeta = {
    ...doc,
    layers: insertLayerAt(doc.layers, insertAboveActive(doc), layer),
    activeLayerId: layer.id,
  };

  state().setDocument(next);
  engine.resourcesFor(next);
  drawPixelsIntoLayer(
    engine,
    next,
    layer.id,
    { width: copied.rect.width, height: copied.rect.height, data: copied.data },
    { x: copied.rect.x, y: copied.rect.y },
  );

  commitHistory(doc.id, 'Layer Via Copy', [
    surfaceOp(layer.id, null, engine.snapshotSurface(next, layer.id)),
    metaOp(cloneDocumentMeta(doc), cloneDocumentMeta(next)),
  ]);
}

/**
 * Ctrl+Shift+J — Layer Via Cut when there is a selection; otherwise moves the
 * whole layer's pixels onto a new layer and clears the source.
 */
export function layerViaCut(): void {
  const doc = activeDocument();
  if (!doc) return;

  const layerId = activeLayerId(doc);
  if (!layerId) return;

  const copied = copyPixels(engine, doc, layerId);
  if (!copied) {
    state().setStatus('There is nothing to cut');
    return;
  }

  const layer = createLayer(nextLayerName(doc));
  const next: DocumentMeta = {
    ...doc,
    layers: insertLayerAt(doc.layers, insertAboveActive(doc), layer),
    activeLayerId: layer.id,
  };

  state().setDocument(next);
  engine.resourcesFor(next);
  drawPixelsIntoLayer(
    engine,
    next,
    layer.id,
    { width: copied.rect.width, height: copied.rect.height, data: copied.data },
    { x: copied.rect.x, y: copied.rect.y },
  );

  const clearOps = clearSelectionPixels(engine, next, layerId) ?? [];
  commitHistory(doc.id, 'Layer Via Cut', [
    surfaceOp(layer.id, null, engine.snapshotSurface(next, layer.id)),
    ...clearOps,
    metaOp(cloneDocumentMeta(doc), cloneDocumentMeta(next)),
  ]);
}

export type LayerStackNudge = 'forward' | 'backward' | 'front' | 'back';

/** Layer > Arrange — same stacking rules as Photoshop's Ctrl+[ / ] family. */
export function nudgeActiveLayer(direction: LayerStackNudge): void {
  const doc = activeDocument();
  if (!doc?.activeLayerId) return;
  const location = locateLayer(doc.layers, doc.activeLayerId);
  if (!location) return;

  const parentId = location.parent?.id ?? null;
  const length = location.siblings.length;
  let index: number;
  if (direction === 'forward') {
    if (location.index >= length - 1) return;
    index = location.index + 2;
  } else if (direction === 'backward') {
    if (location.index <= 0) return;
    index = location.index - 1;
  } else if (direction === 'front') {
    if (location.index >= length - 1) return;
    index = length;
  } else {
    if (location.index <= 0) return;
    index = 0;
  }
  moveLayerTo(doc.activeLayerId, { parentId, index });
}

/** Alt+[ / ] — walk the flat layer stack, Photoshop-style. */
export function selectAdjacentLayer(delta: 1 | -1): void {
  const doc = activeDocument();
  if (!doc) return;
  const flat = flattenLayers(doc.layers);
  if (flat.length === 0) return;
  const current = Math.max(
    0,
    flat.findIndex((layer) => layer.id === doc.activeLayerId),
  );
  const next = clamp(current + delta, 0, flat.length - 1);
  if (flat[next]) setActiveLayer(flat[next].id);
}

/** Alt+. / Alt+, — jump to the top or bottom of the layer stack. */
export function selectStackEdge(edge: 'top' | 'bottom'): void {
  const doc = activeDocument();
  if (!doc) return;
  const flat = flattenLayers(doc.layers);
  if (flat.length === 0) return;
  const layer = edge === 'top' ? flat[flat.length - 1] : flat[0];
  if (layer) setActiveLayer(layer.id);
}

/** Moves a layer to another slot, which may be inside or outside a group. */
export function moveLayerTo(layerId: string, target: InsertTarget): void {
  const doc = activeDocument();
  if (!doc) return;
  const layers = moveLayerInTree(doc.layers, layerId, target);
  if (layers === doc.layers) return;
  commitMeta(doc, 'Reorder Layer', { ...doc, layers });
}

/** Layer > Group Layers. Wraps the active layer in a new group in its place. */
export function groupActiveLayer(): void {
  const doc = activeDocument();
  if (!doc) return;
  const location = locateLayer(doc.layers, doc.activeLayerId ?? '');
  if (!location) return;

  const group = createGroupLayer(nextLayerName(doc, 'Group'), [location.layer]);
  const detached = removeLayerFromTree(doc.layers, location.layer.id).layers;
  const layers = insertLayerAt(
    detached,
    { parentId: location.parent?.id ?? null, index: location.index },
    group,
  );
  commitMeta(doc, 'Group Layers', { ...doc, layers, activeLayerId: group.id });
}

/** Layer > Ungroup Layers. Splices the children back into the parent list. */
export function ungroupActiveLayer(): void {
  const doc = activeDocument();
  if (!doc) return;
  const location = locateLayer(doc.layers, doc.activeLayerId ?? '');
  if (!location || !isGroup(location.layer)) {
    state().setStatus('Select a group to ungroup');
    return;
  }

  const children = location.layer.children;
  let layers = removeLayerFromTree(doc.layers, location.layer.id).layers;
  const parentId = location.parent?.id ?? null;
  children.forEach((child, offset) => {
    layers = insertLayerAt(layers, { parentId, index: location.index + offset }, child);
  });
  commitMeta(doc, 'Ungroup Layers', {
    ...doc,
    layers,
    activeLayerId: children[children.length - 1]?.id ?? parentId,
  });
}

/** Layer > Create/Release Clipping Mask. */
export function toggleClippingMask(layerId: string): void {
  const doc = activeDocument();
  if (!doc) return;
  const location = locateLayer(doc.layers, layerId);
  if (!location) return;
  if (location.index === 0 && !location.layer.clipping) {
    state().setStatus('A clipping mask needs a layer below it');
    return;
  }
  patchLayer(
    layerId,
    { clipping: !location.layer.clipping },
    location.layer.clipping ? 'Release Clipping Mask' : 'Create Clipping Mask',
  );
}

/** Layer > New Adjustment Layer. Corrects everything composited below it. */
export function addAdjustmentLayer(adjustment: AdjustmentId): void {
  const doc = activeDocument();
  if (!doc) return;
  const definition = ADJUSTMENTS[adjustment];
  const layer = createAdjustmentLayer(definition.label, defaultAdjustmentState(adjustment));
  const layers = insertLayerAt(doc.layers, insertAboveActive(doc), layer);
  commitMeta(doc, `New ${definition.label} Layer`, {
    ...doc,
    layers,
    activeLayerId: layer.id,
  });
  state().openPanel('properties');
}

/** Live edits from the Properties panel; commit with a meta transaction. */
export function setAdjustmentLayerState(
  layerId: string,
  adjustment: AdjustmentState,
  commit: boolean,
): void {
  const doc = activeDocument();
  if (!doc) return;
  const update = (layer: Layer): Layer =>
    layer.kind === 'adjustment' ? ({ ...layer, adjustment } satisfies AdjustmentLayer) : layer;

  if (!commit) {
    state().replaceDocument(doc.id, (current) => ({
      ...current,
      layers: mapLayer(current.layers, layerId, update),
    }));
    engine.invalidate();
    return;
  }
  commitMeta(doc, 'Adjustment Layer', { ...doc, layers: mapLayer(doc.layers, layerId, update) });
}

export type FillLayerKind = FillContent['kind'];

/** Defaults for Layer > New Fill Layer. */
export function defaultFillContent(kind: FillLayerKind): FillContent {
  if (kind === 'solid') {
    return { kind: 'solid', color: { ...state().foreground } };
  }
  if (kind === 'gradient') {
    return {
      kind: 'gradient',
      gradient: state().gradient.value ?? BUILTIN_GRADIENTS[0],
      type: state().gradient.type ?? 'linear',
      angle: 90,
      scale: 100,
      reverse: false,
    };
  }
  return {
    kind: 'pattern',
    patternId: state().activePatternId ?? allPatterns()[0]?.id ?? '',
    scale: 100,
  };
}

const FILL_LABELS: Record<FillLayerKind, string> = {
  solid: 'Solid Color',
  gradient: 'Gradient',
  pattern: 'Pattern',
};

/** Layer > New Fill Layer. */
export function addFillLayer(kind: FillLayerKind): void {
  const doc = activeDocument();
  if (!doc) return;
  const label = FILL_LABELS[kind];
  const layer = createFillLayer(label, defaultFillContent(kind));
  const layers = insertLayerAt(doc.layers, insertAboveActive(doc), layer);
  commitMeta(doc, `New ${label} Fill Layer`, {
    ...doc,
    layers,
    activeLayerId: layer.id,
  });
  state().openPanel('properties');
}

/** Live edits from the Properties panel for a fill layer. */
export function setFillLayerContent(
  layerId: string,
  fill: FillContent,
  commit: boolean,
): void {
  const doc = activeDocument();
  if (!doc) return;
  const update = (layer: Layer): Layer =>
    layer.kind === 'fill' ? ({ ...layer, fill } satisfies FillLayer) : layer;

  if (!commit) {
    state().replaceDocument(doc.id, (current) => ({
      ...current,
      layers: mapLayer(current.layers, layerId, update),
    }));
    engine.invalidate();
    return;
  }
  commitMeta(doc, 'Fill Layer', { ...doc, layers: mapLayer(doc.layers, layerId, update) });
}

/** Purely a view state of the layers panel, so it stays out of history. */
export function toggleGroupExpanded(layerId: string): void {
  const doc = activeDocument();
  if (!doc) return;
  state().replaceDocument(doc.id, (current) => ({
    ...current,
    layers: mapLayer(current.layers, layerId, (layer) =>
      isGroup(layer) ? { ...layer, expanded: !layer.expanded } : layer,
    ),
  }));
}

export function setActiveLayer(layerId: string): void {
  const doc = activeDocument();
  if (!doc || doc.activeLayerId === layerId) return;
  state().replaceDocument(doc.id, (current) => ({ ...current, activeLayerId: layerId }));
}

/** Merges a pixel layer into the pixel layer directly below it in its group. */
export function mergeDown(layerId: string): void {
  const doc = activeDocument();
  if (!doc) return;
  const location = locateLayer(doc.layers, layerId);
  if (!location || location.index <= 0) return;
  const lower = location.siblings[location.index - 1];
  if (!isSurfaceLayer(location.layer) || !isSurfaceLayer(lower)) {
    state().setStatus('Merge Down works between two pixel layers');
    return;
  }

  const pixelOps = mergeLayerDown(engine, doc, layerId, lower.id);
  if (!pixelOps) return;

  const surfaceBefore = engine.snapshotSurface(doc, layerId);
  const layers = removeLayerFromTree(doc.layers, layerId).layers;
  const next: DocumentMeta = { ...doc, layers, activeLayerId: lower.id };

  state().setDocument(next);
  engine.peek(doc.id)?.deleteLayer(layerId);
  commitHistory(doc.id, 'Merge Down', [
    ...pixelOps,
    surfaceOp(layerId, surfaceBefore, null),
    metaOp(cloneDocumentMeta(doc), cloneDocumentMeta(next)),
  ]);
}

/**
 * Bakes the whole tree into a single pixel layer. The composite goes into a
 * fresh surface, because the bottom of the stack can now be a group.
 */
export function flattenImage(): void {
  const doc = activeDocument();
  if (!doc || countLayers(doc.layers) <= 1) return;
  const flattened = createLayer(doc.layers[0].name);

  const ops: EngineHistoryOp[] = [];
  for (const layer of doc.layers) ops.push(...removalOps(doc, layer));

  // Flatten reads the document as it is, then replaces it wholesale.
  const staged: DocumentMeta = { ...doc, layers: [...doc.layers, flattened] };
  state().setDocument(staged);
  engine.resourcesFor(staged);
  flattenInto(engine, doc, flattened.id);
  ops.push(surfaceOp(flattened.id, null, engine.snapshotSurface(staged, flattened.id)));

  const next: DocumentMeta = { ...doc, layers: [flattened], activeLayerId: flattened.id };
  state().setDocument(next);
  for (const layer of doc.layers) releaseBranch(doc.id, layer);

  commitHistory(doc.id, 'Flatten Image', [
    ...ops,
    metaOp(cloneDocumentMeta(doc), cloneDocumentMeta(next)),
  ]);
}

/**
 * Ctrl+Shift+E — Merge Visible. Stamps the current composite into one layer and
 * removes every previously visible layer from the tree.
 */
export function mergeVisible(): void {
  const doc = activeDocument();
  if (!doc) return;
  const visibleIds = flattenLayers(doc.layers)
    .filter((layer) => layer.visible)
    .map((layer) => layer.id);
  if (visibleIds.length === 0) return;

  const merged = createLayer('Visible');
  const staged: DocumentMeta = {
    ...doc,
    layers: insertLayerAt(doc.layers, { parentId: null, index: doc.layers.length }, merged),
    activeLayerId: merged.id,
  };
  state().setDocument(staged);
  engine.resourcesFor(staged);
  // Composite the pre-stamp document so the empty stamp layer is not included.
  const pixelOps = flattenInto(engine, doc, merged.id);

  const removal: EngineHistoryOp[] = [];
  let layers = staged.layers;
  for (const id of visibleIds) {
    const found = findLayerById(layers, id);
    if (!found) continue;
    removal.push(...removalOps({ ...doc, layers }, found));
    layers = removeLayerFromTree(layers, id).layers;
    releaseBranch(doc.id, found);
  }

  const next: DocumentMeta = { ...doc, layers, activeLayerId: merged.id };
  state().setDocument(next);
  commitHistory(doc.id, 'Merge Visible', [
    ...pixelOps,
    ...removal,
    metaOp(cloneDocumentMeta(doc), cloneDocumentMeta(next)),
  ]);
}

/** Ctrl+Alt+Shift+E — stamp a merged copy of all visible layers onto a new layer. */
export function stampVisible(): void {
  const doc = activeDocument();
  if (!doc) return;
  const layer = createLayer(nextLayerName(doc));
  const next: DocumentMeta = {
    ...doc,
    layers: insertLayerAt(doc.layers, insertAboveActive(doc), layer),
    activeLayerId: layer.id,
  };
  state().setDocument(next);
  engine.resourcesFor(next);
  const pixelOps = flattenInto(engine, doc, layer.id);
  commitHistory(doc.id, 'Stamp Visible', [
    ...pixelOps,
    metaOp(cloneDocumentMeta(doc), cloneDocumentMeta(next)),
  ]);
}

/** Ctrl+H — show or hide the marching ants (View > Extras). */
export function toggleSelectionEdges(): void {
  liveRender.showSelection = !liveRender.showSelection;
  engine.invalidate();
  state().setStatus(liveRender.showSelection ? 'Extras visible' : 'Extras hidden');
}

// --- Selection ---

export function applySelectionShape(raster: ShapeRaster, mode: SelectionCombineMode): void {
  const doc = activeDocument();
  if (!doc) return;
  const resources = engine.resourcesFor(doc);
  const before = engine.snapshotSelection(doc);
  resources.selection.combineShape(
    engine.renderer,
    raster,
    mode,
    state().toolOptions.selectionFeather,
  );
  resources.selection.refresh();
  const after = engine.snapshotSelection(doc);
  syncSelectionFlag(doc.id);
  commitHistory(doc.id, 'Selection', [selectionOp(before, after)]);
}

export function selectAll(): void {
  const doc = activeDocument();
  if (!doc) return;
  const resources = engine.resourcesFor(doc);
  const before = engine.snapshotSelection(doc);
  resources.selection.selectAll();
  syncSelectionFlag(doc.id);
  commitHistory(doc.id, 'Select All', [selectionOp(before, engine.snapshotSelection(doc))]);
}

export function deselect(): void {
  const doc = activeDocument();
  if (!doc) return;
  const resources = engine.resourcesFor(doc);
  if (resources.selection.isEmpty) return;
  const before = engine.snapshotSelection(doc);
  resources.selection.deselect();
  syncSelectionFlag(doc.id);
  commitHistory(doc.id, 'Deselect', [selectionOp(before, null)]);
}

export function invertSelection(): void {
  const doc = activeDocument();
  if (!doc) return;
  const resources = engine.resourcesFor(doc);
  const before = engine.snapshotSelection(doc);
  resources.selection.invert(engine.renderer);
  resources.selection.refresh();
  syncSelectionFlag(doc.id);
  commitHistory(doc.id, 'Inverse Selection', [
    selectionOp(before, engine.snapshotSelection(doc)),
  ]);
}

export function selectionBounds(): Rect | null {
  const doc = activeDocument();
  if (!doc) return null;
  return engine.resourcesFor(doc).selection.bounds();
}

/** Box around a layer's own pixels, which is what a transform box hugs. */
export function layerContentBounds(layerId: string): Rect | null {
  const doc = activeDocument();
  if (!doc) return null;
  return engine.contentBounds(doc, layerId);
}

// --- Painting and editing ---

export function fillWithColor(color: RGBA): void {
  const doc = activeDocument();
  if (!doc) return;
  const layerId = activeLayerId(doc);
  if (!layerId) return;
  commitHistory(doc.id, 'Fill', fillSelection(engine, doc, layerId, color));
}

export function clearPixels(): void {
  const doc = activeDocument();
  if (!doc) return;
  const layerId = activeLayerId(doc);
  if (!layerId) return;
  commitHistory(doc.id, 'Clear', clearSelectionPixels(engine, doc, layerId));
}

export function bucketFillAt(point: Point): void {
  const doc = activeDocument();
  if (!doc) return;
  const layerId = activeLayerId(doc);
  if (!layerId) return;
  const { toolOptions, foreground } = state();
  commitHistory(
    doc.id,
    'Paint Bucket',
    bucketFill(engine, doc, layerId, point, foreground, {
      tolerance: toolOptions.bucketTolerance,
      contiguous: toolOptions.bucketContiguous,
      opacity: 1,
    }),
  );
}

/**
 * Applies a free-transform. Pure translations update contentOffset so pixels
 * past the canvas edge survive; anything else is baked into the layer texture.
 */
export function commitTransform(layerId: string, srcMatrix: Float32Array): void {
  const doc = activeDocument();
  if (!doc) return;
  const translation = srcUvPureTranslation(srcMatrix, doc.width, doc.height);
  if (translation) {
    nudgeLayerOffset(layerId, translation.x, translation.y, 'Free Transform');
    return;
  }
  const layer = findLayerById(doc.layers, layerId);
  if (!layer) return;
  const offset = layer.contentOffset ?? { x: 0, y: 0 };
  const composed =
    composeSrcMatrices(
      contentOffsetSrcMatrix(offset, doc.width, doc.height),
      srcMatrix,
    ) ?? srcMatrix;
  const ops = commitLayerTransform(engine, doc, layerId, composed);
  if (!ops) return;
  if (offset.x !== 0 || offset.y !== 0) {
    const next: DocumentMeta = {
      ...doc,
      layers: mapLayer(doc.layers, layerId, (entry) =>
        applyLayerPatch(entry, { contentOffset: { x: 0, y: 0 } }),
      ),
    };
    commitMeta(doc, 'Free Transform', next, ops);
    return;
  }
  commitHistory(doc.id, 'Free Transform', ops);
}

/**
 * Pure translation for raster/smart layers via contentOffset so pixels past the
 * canvas edge survive. Callers handle shape geometry and text baselines first.
 */
export function nudgeLayerOffset(
  layerId: string,
  dx: number,
  dy: number,
  label = 'Move Layer',
): boolean {
  const doc = activeDocument();
  if (!doc) return false;
  const x = Math.round(dx);
  const y = Math.round(dy);
  if (x === 0 && y === 0) return false;
  const layer = findLayerById(doc.layers, layerId);
  if (!layer || !isSurfaceLayer(layer)) return false;
  if (layer.kind === 'shape' || layer.kind === 'text') return false;
  if (layer.lockPosition || layer.locked) return false;
  const prev = layer.contentOffset ?? { x: 0, y: 0 };
  patchLayer(layerId, { contentOffset: { x: prev.x + x, y: prev.y + y } }, label);
  return true;
}

/** Live warp preview: the warped copy stands in for the layer's own pixels. */
export function previewWarp(layerId: string, rect: Rect, mesh: WarpMesh): void {
  const doc = activeDocument();
  if (!doc) return;
  const texture = renderWarpPreview(engine, doc, layerId, rect, mesh);
  setLivePreview('surface', layerId, texture);
}

export function commitWarp(layerId: string, rect: Rect, mesh: WarpMesh): void {
  const doc = activeDocument();
  if (!doc) return;
  setLivePreview('surface', layerId, null);
  commitHistory(doc.id, 'Warp', commitLayerWarp(engine, doc, layerId, rect, mesh));
}

// --- Guides ---

export function addGuide(axis: Guide['axis'], position: number): Guide | null {
  const doc = activeDocument();
  if (!doc) return null;
  const guide = createGuide(axis, Math.round(position));
  commitMeta(doc, 'New Guide', { ...doc, guides: [...doc.guides, guide] });
  return guide;
}

/** Adds a guide without a history step, for a drag out of a ruler. */
export function addGuideTransient(axis: Guide['axis'], position: number): Guide | null {
  const doc = activeDocument();
  if (!doc) return null;
  const guide = createGuide(axis, Math.round(position));
  state().replaceDocument(doc.id, (current) => ({
    ...current,
    guides: [...current.guides, guide],
  }));
  engine.invalidate('overlay');
  return guide;
}

/** Transient move while a guide is being dragged; pair with a meta transaction. */
export function moveGuideTransient(guideId: string, position: number): void {
  const doc = activeDocument();
  if (!doc) return;
  state().replaceDocument(doc.id, (current) => ({
    ...current,
    guides: current.guides.map((guide) =>
      guide.id === guideId ? { ...guide, position: Math.round(position) } : guide,
    ),
  }));
  engine.invalidate('overlay');
}

export function removeGuide(guideId: string): void {
  const doc = activeDocument();
  if (!doc) return;
  commitMeta(doc, 'Delete Guide', {
    ...doc,
    guides: doc.guides.filter((guide) => guide.id !== guideId),
  });
}

/** Removes a guide without a history step, to end a drag that left the canvas. */
export function removeGuideTransient(guideId: string): void {
  const doc = activeDocument();
  if (!doc) return;
  state().replaceDocument(doc.id, (current) => ({
    ...current,
    guides: current.guides.filter((guide) => guide.id !== guideId),
  }));
  engine.invalidate('overlay');
}

export function clearGuides(): void {
  const doc = activeDocument();
  if (!doc || doc.guides.length === 0) return;
  commitMeta(doc, 'Clear Guides', { ...doc, guides: [] });
}

/** View - New Guide Layout: the thirds grid, which is the common case. */
export function addGuidesFromSelection(): void {
  const doc = activeDocument();
  if (!doc) return;
  const bounds = selectionBounds();
  if (!bounds) return;
  const added = [
    createGuide('vertical', Math.round(bounds.x)),
    createGuide('vertical', Math.round(bounds.x + bounds.width)),
    createGuide('horizontal', Math.round(bounds.y)),
    createGuide('horizontal', Math.round(bounds.y + bounds.height)),
  ];
  commitMeta(doc, 'Guides from Selection', { ...doc, guides: [...doc.guides, ...added] });
}

/** Lines the current drag can latch onto, from the View menu settings. */
export function activeSnapTargets(): SnapTargets {
  const doc = activeDocument();
  const store = state();
  if (!doc) return EMPTY_TARGETS;
  return snapTargets(doc, doc.guides, store.grid, store.snap);
}

/**
 * Move tool with a live selection: the selected pixels come off the layer and
 * follow the pointer. `copy` leaves the originals behind, as Alt-dragging does.
 */
export function liftFloatingSelection(layerId: string, copy: boolean): FloatingSelection | null {
  const doc = activeDocument();
  if (!doc) return null;
  return FloatingSelection.lift(engine, doc, layerId, copy);
}

export function dropFloatingSelection(
  float: FloatingSelection,
  layerId: string,
  offset: Point,
  record: boolean,
): void {
  const doc = activeDocument();
  if (!doc) {
    float.dispose();
    return;
  }
  const ops = float.drop(doc, layerId, offset);
  float.dispose();
  if (!record) {
    engine.invalidate();
    return;
  }
  syncSelectionFlag(doc.id);
  commitHistory(doc.id, 'Move Selection', ops);
}

export function commitSelectionTransform(srcMatrix: Float32Array): void {
  const doc = activeDocument();
  if (!doc) return;
  const ops = transformSelectionMask(engine, doc, srcMatrix);
  syncSelectionFlag(doc.id);
  commitHistory(doc.id, 'Transform Selection', ops);
}

// --- Clipboard ---

/** Edit > Copy, and Copy Merged when `merged` is set. */
export function copySelection(merged = false): void {
  const doc = activeDocument();
  if (!doc) return;
  const active = findLayerById(doc.layers, doc.activeLayerId);
  if (!merged && (!active || !isSurfaceLayer(active))) {
    state().setStatus('Select a pixel layer to copy from, or use Copy Merged');
    return;
  }
  const layerId = merged ? null : (active as SurfaceLayer).id;

  const copied = copyPixels(engine, doc, layerId);
  if (!copied) {
    state().setStatus('There is nothing to copy');
    return;
  }
  setInternalClipboard(copied);
  state().setStatus(merged ? 'Copied merged' : 'Copied');
  void writeImageToSystemClipboard(copied);
}

export function cutSelection(): void {
  const doc = activeDocument();
  if (!doc) return;
  const layerId = activeLayerId(doc);
  if (!layerId) return;

  const copied = copyPixels(engine, doc, layerId);
  if (!copied) return;
  setInternalClipboard(copied);
  void writeImageToSystemClipboard(copied);
  commitHistory(doc.id, 'Cut', clearSelectionPixels(engine, doc, layerId));
}

/**
 * Edit > Paste. Every in-app copy is mirrored onto the system clipboard, so the
 * system clipboard is the authority on what was copied last: an image that is
 * not our own mirror was put there afterwards and retires the in-app copy. The
 * in-app copy is still preferred when it is the current one, because it carries
 * the original position that Paste in Place needs.
 */
export async function pasteClipboard(inPlace = false): Promise<void> {
  const doc = activeDocument();
  if (!doc) return;

  const system = await readImageFromSystemClipboard();
  if (system && !isOwnMirror(system.size)) setInternalClipboard(null);

  const internal = getInternalClipboard();
  if (internal) {
    system?.image.release();
    pasteIntoNewLayer(doc, internal.rect, inPlace, (target, layer, at) =>
      drawPixelsIntoLayer(engine, target, layer, { ...internal.rect, data: internal.data }, at),
    );
    return;
  }

  if (!system) {
    state().setStatus('The clipboard has no image');
    return;
  }
  const image = system.image;
  try {
    const box = { x: 0, y: 0, width: image.width, height: image.height };
    pasteIntoNewLayer(doc, box, inPlace, (target, layer, at) =>
      uploadImageToLayer(engine, target, layer, image.source, {
        x: at.x,
        y: at.y,
        width: image.width,
        height: image.height,
      }),
    );
  } finally {
    image.release();
  }
}

function pasteIntoNewLayer(
  doc: DocumentMeta,
  box: Rect,
  inPlace: boolean,
  draw: (doc: DocumentMeta, layerId: string, at: Point) => void,
): void {
  const layer = createLayer(nextLayerName(doc));
  const next: DocumentMeta = {
    ...doc,
    layers: insertLayerAt(doc.layers, insertAboveActive(doc), layer),
    activeLayerId: layer.id,
  };
  const at = inPlace
    ? { x: box.x, y: box.y }
    : { x: Math.round((doc.width - box.width) / 2), y: Math.round((doc.height - box.height) / 2) };

  state().setDocument(next);
  engine.resourcesFor(next);
  draw(next, layer.id, at);

  // Pasted content lands on its own layer, so the old marching ants would only
  // be misleading.
  const resources = engine.resourcesFor(next);
  const selectionBefore = engine.snapshotSelection(next);
  resources.selection.deselect();
  syncSelectionFlag(next.id);

  commitHistory(doc.id, inPlace ? 'Paste in Place' : 'Paste', [
    surfaceOp(layer.id, null, engine.snapshotSurface(next, layer.id)),
    ...(selectionBefore ? [selectionOp(selectionBefore, null)] : []),
    metaOp(cloneDocumentMeta(doc), cloneDocumentMeta(next)),
  ]);
}

// --- Fill and stroke ---

export type FillContents =
  | 'foreground'
  | 'background'
  | 'black'
  | 'white'
  | 'gray'
  | 'transparent';

export function fillColorFor(contents: FillContents): RGBA {
  const { foreground, background } = state();
  switch (contents) {
    case 'foreground':
      return foreground;
    case 'background':
      return background;
    case 'black':
      return BLACK;
    case 'white':
      return WHITE;
    case 'gray':
      return { r: 0.5, g: 0.5, b: 0.5, a: 1 };
    case 'transparent':
      return TRANSPARENT;
  }
}

export function applyFill(
  contents: FillContents | 'color',
  opacity: number,
  options: { blendMode?: BlendMode; color?: RGBA } = {},
): void {
  const doc = activeDocument();
  if (!doc) return;
  const layerId = activeLayerId(doc);
  if (!layerId) return;

  if (contents === 'transparent') {
    commitHistory(doc.id, 'Fill', clearSelectionPixels(engine, doc, layerId));
    return;
  }
  const color =
    contents === 'color' ? (options.color ?? state().foreground) : fillColorFor(contents);
  commitHistory(
    doc.id,
    'Fill',
    fillSelection(engine, doc, layerId, color, opacity, options.blendMode ?? 'normal'),
  );
}

export function applyStroke(options: {
  thickness: number;
  location: StrokeLocation;
  contents: FillContents;
  opacity: number;
}): void {
  const doc = activeDocument();
  if (!doc) return;
  const layerId = activeLayerId(doc);
  if (!layerId) return;
  if (!doc.hasSelection) {
    state().setStatus('Stroke needs a selection');
    return;
  }
  commitHistory(
    doc.id,
    'Stroke',
    strokeSelection(engine, doc, layerId, {
      thickness: options.thickness,
      location: options.location,
      color: fillColorFor(options.contents),
      opacity: options.opacity,
    }),
  );
}

// --- Adjustments ---

export function applyAdjustment(adjustment: AdjustmentState): void {
  const doc = activeDocument();
  if (!doc) return;
  const layerId = activeLayerId(doc);
  if (!layerId) return;
  commitHistory(
    doc.id,
    ADJUSTMENTS[adjustment.id].label,
    applyAdjustmentOp(engine, doc, layerId, adjustment),
  );
}

export function applyFilterValues(id: FilterId, values: Record<string, number>): void {
  const doc = activeDocument();
  if (!doc) return;
  const layerId = activeLayerId(doc);
  if (!layerId) return;
  const layer = findLayerById(doc.layers, layerId);
  if (layer?.kind === 'smart') {
    const entry = makeSmartFilter(id, values);
    commitMeta(doc, `Smart Filter: ${FILTERS[id].label}`, {
      ...doc,
      layers: mapLayer(doc.layers, layerId, (current) =>
        current.kind === 'smart'
          ? { ...current, smartFilters: [...current.smartFilters, entry] }
          : current,
      ),
    });
    return;
  }
  commitHistory(doc.id, FILTERS[id].label, applyFilterOp(engine, doc, layerId, id, values));
}

// --- Layer styles ---

export function setLayerEffects(
  layerId: string,
  effects: LayerEffects | null,
  commit: boolean,
): void {
  const doc = activeDocument();
  if (!doc) return;
  if (!commit) {
    patchLayerTransient(layerId, { effects });
    return;
  }
  patchLayer(layerId, { effects }, 'Layer Style');
}

export function clearLayerEffects(layerId: string): void {
  setLayerEffects(layerId, null, true);
}

export function applyLayerEffectsPreset(layerId: string, effects: LayerEffects): void {
  setLayerEffects(layerId, effects, true);
}

// --- Smart objects ---

export function convertToSmartObject(): void {
  const doc = activeDocument();
  if (!doc?.activeLayerId) return;
  const layer = findLayerById(doc.layers, doc.activeLayerId);
  if (!layer || !isSurfaceLayer(layer) || layer.kind === 'smart') return;
  const smart = toSmartLayer(layer);
  const next: DocumentMeta = {
    ...doc,
    layers: mapLayer(doc.layers, layer.id, () => smart),
  };
  commitMeta(doc, 'Convert to Smart Object', next);
  captureSmartSource(engine, next, smart.id);
}

export function rasterizeSmartObject(): void {
  const doc = activeDocument();
  if (!doc?.activeLayerId) return;
  const layer = findLayerById(doc.layers, doc.activeLayerId);
  if (!layer || layer.kind !== 'smart') return;
  bakeSmartLayer(engine, doc, layer);
  commitMeta(doc, 'Rasterize Smart Object', {
    ...doc,
    layers: mapLayer(doc.layers, layer.id, (current) =>
      current.kind === 'smart' ? toRasterFromSmart(current) : current,
    ),
  });
}

export function setSmartFilters(layerId: string, filters: SmartFilter[]): void {
  const doc = activeDocument();
  if (!doc) return;
  commitMeta(doc, 'Smart Filters', {
    ...doc,
    layers: mapLayer(doc.layers, layerId, (layer) =>
      layer.kind === 'smart' ? { ...layer, smartFilters: filters } : layer,
    ),
  });
}

/** Places the active layer's pixels into a fresh smart object above it. */
export function newSmartObjectFromLayer(): void {
  const doc = activeDocument();
  if (!doc?.activeLayerId) return;
  const source = findLayerById(doc.layers, doc.activeLayerId);
  if (!source || !isSurfaceLayer(source)) return;
  const smart = createSmartLayer(nextLayerName(doc, 'Smart Object'));
  const layers = insertLayerAt(doc.layers, insertAboveActive(doc), smart);
  const next = { ...doc, layers, activeLayerId: smart.id };
  commitMeta(doc, 'New Smart Object', next);
  const resources = engine.resourcesFor(next);
  const from = resources.ensureLayer(source.id).surface.texture;
  engine.renderer.copy(resources.ensureLayer(smart.id).surface.target.framebuffer, from);
  captureSmartSource(engine, next, smart.id);
}

// --- Document geometry ---

function mapLayerTree(layers: readonly Layer[], transform: (layer: Layer) => Layer): Layer[] {
  return layers.map((layer) => {
    const mapped = transform(layer);
    if (!isGroup(mapped)) return mapped;
    return { ...mapped, children: mapLayerTree(mapped.children, transform) };
  });
}

/**
 * Carries vector geometry through a resize or a crop. The pixels are resampled by
 * the engine, so this only keeps the paths behind them in step; without it a
 * shape would jump back to its old size the next time it was redrawn.
 */
function remapVectors(
  doc: DocumentMeta,
  matrix: Mat3,
): Pick<DocumentMeta, 'layers' | 'paths' | 'guides'> {
  const layers = mapLayerTree(doc.layers, (layer) => {
    const vectorMask = layer.vectorMask ? transformSubpaths(layer.vectorMask, matrix) : null;
    if (layer.kind !== 'shape') return layer.vectorMask ? { ...layer, vectorMask } : layer;
    return {
      ...layer,
      vectorMask,
      geometry: {
        kind: 'freeform',
        subpaths: transformSubpaths(geometryToSubpaths(layer.geometry), matrix),
      },
    };
  });
  const paths = doc.paths.map((path) => ({
    ...path,
    subpaths: transformSubpaths(path.subpaths, matrix),
  }));
  // A guide only has one coordinate, so it follows the matrix along its own axis.
  const guides = doc.guides.map((guide) => {
    const moved =
      guide.axis === 'horizontal'
        ? mat3TransformPoint(matrix, 0, guide.position).y
        : mat3TransformPoint(matrix, guide.position, 0).x;
    return { ...guide, position: moved };
  });
  return { layers, paths, guides };
}

export function applyImageSize(width: number, height: number): void {
  const doc = activeDocument();
  if (!doc) return;
  const maxSize = engine.caps.maxTextureSize;
  const nextWidth = clamp(Math.round(width), 1, maxSize);
  const nextHeight = clamp(Math.round(height), 1, maxSize);
  if (nextWidth === doc.width && nextHeight === doc.height) return;

  const ops = resampleDocument(engine, doc, nextWidth, nextHeight);
  const next: DocumentMeta = {
    ...doc,
    width: nextWidth,
    height: nextHeight,
    ...remapVectors(doc, mat3Scaling(nextWidth / doc.width, nextHeight / doc.height)),
  };
  state().setDocument(next);
  state().setView(doc.id, {
    zoom: fitZoom(engine.canvasSize, nextWidth, nextHeight),
    centerX: nextWidth / 2,
    centerY: nextHeight / 2,
    rotation: 0,
  });
  commitHistory(doc.id, 'Image Size', [
    ...ops,
    metaOp(cloneDocumentMeta(doc), cloneDocumentMeta(next)),
  ]);
  syncSelectionFlag(doc.id);
}

export type CanvasAnchor =
  | 'topLeft'
  | 'top'
  | 'topRight'
  | 'left'
  | 'center'
  | 'right'
  | 'bottomLeft'
  | 'bottom'
  | 'bottomRight';

function anchorOffset(
  anchor: CanvasAnchor,
  oldWidth: number,
  oldHeight: number,
  newWidth: number,
  newHeight: number,
): Point {
  const dx = newWidth - oldWidth;
  const dy = newHeight - oldHeight;
  const horizontal = anchor.includes('Left') || anchor === 'left' ? 0 : anchor.includes('Right') || anchor === 'right' ? dx : dx / 2;
  const vertical = anchor.startsWith('top') ? 0 : anchor.startsWith('bottom') ? dy : dy / 2;
  return { x: horizontal, y: vertical };
}

export function applyCanvasSize(width: number, height: number, anchor: CanvasAnchor): void {
  const doc = activeDocument();
  if (!doc) return;
  const maxSize = engine.caps.maxTextureSize;
  const nextWidth = clamp(Math.round(width), 1, maxSize);
  const nextHeight = clamp(Math.round(height), 1, maxSize);
  if (nextWidth === doc.width && nextHeight === doc.height) return;

  const offset = anchorOffset(anchor, doc.width, doc.height, nextWidth, nextHeight);
  applyRecanvas(doc, nextWidth, nextHeight, offset.x, offset.y, 'Canvas Size');
}

export function applyCrop(rect: Rect): void {
  const doc = activeDocument();
  if (!doc) return;
  const width = Math.max(1, Math.round(rect.width));
  const height = Math.max(1, Math.round(rect.height));
  applyRecanvas(doc, width, height, -Math.round(rect.x), -Math.round(rect.y), 'Crop');
}

/**
 * Image → Crop: trims the canvas to the selection bounds and clears the
 * outline, matching Photoshop's Crop when a selection is active.
 */
export function cropToSelection(): void {
  const doc = activeDocument();
  if (!doc) return;
  const bounds = selectionBounds();
  if (!bounds || bounds.width < 1 || bounds.height < 1) {
    state().setStatus('Make a selection to crop');
    return;
  }

  const width = Math.max(1, Math.round(bounds.width));
  const height = Math.max(1, Math.round(bounds.height));
  const offsetX = -Math.round(bounds.x);
  const offsetY = -Math.round(bounds.y);
  const selectionBefore = engine.snapshotSelection(doc);
  const ops = recanvasDocument(engine, doc, width, height, offsetX, offsetY);
  engine.resourcesFor(doc).selection.deselect();

  const next: DocumentMeta = {
    ...doc,
    width,
    height,
    hasSelection: false,
    ...remapVectors(doc, mat3Translation(offsetX, offsetY)),
  };
  state().setDocument(next);
  state().setView(doc.id, {
    zoom: fitZoom(engine.canvasSize, width, height),
    centerX: width / 2,
    centerY: height / 2,
    rotation: 0,
  });
  commitHistory(doc.id, 'Crop', [
    ...ops,
    selectionOp(selectionBefore, null),
    metaOp(cloneDocumentMeta(doc), cloneDocumentMeta(next)),
  ]);
  syncSelectionFlag(doc.id);
}

function applyRecanvas(
  doc: DocumentMeta,
  width: number,
  height: number,
  offsetX: number,
  offsetY: number,
  label: string,
): void {
  const ops = recanvasDocument(engine, doc, width, height, offsetX, offsetY);
  const next: DocumentMeta = {
    ...doc,
    width,
    height,
    ...remapVectors(doc, mat3Translation(offsetX, offsetY)),
  };
  state().setDocument(next);
  state().setView(doc.id, {
    zoom: fitZoom(engine.canvasSize, width, height),
    centerX: width / 2,
    centerY: height / 2,
    rotation: 0,
  });
  commitHistory(doc.id, label, [...ops, metaOp(cloneDocumentMeta(doc), cloneDocumentMeta(next))]);
  syncSelectionFlag(doc.id);
}

// --- History ---

export function undo(): void {
  const doc = activeDocument();
  if (!doc) return;
  const resources = engine.peek(doc.id);
  const step = resources?.history.undo();
  if (!step) return;
  clearLiveOverrides();
  engine.applyHistoryOps(doc, step.ops, 'undo');
  syncHistory(doc.id);
  syncSelectionFlag(doc.id);
  scheduleAutosave(doc.id);
  state().setStatus(`Undo: ${step.label}`);
}

export function redo(): void {
  const doc = activeDocument();
  if (!doc) return;
  const resources = engine.peek(doc.id);
  const step = resources?.history.redo();
  if (!step) return;
  clearLiveOverrides();
  engine.applyHistoryOps(doc, step.ops, 'redo');
  syncHistory(doc.id);
  syncSelectionFlag(doc.id);
  scheduleAutosave(doc.id);
  state().setStatus(`Redo: ${step.label}`);
}

/** Steps history to `targetPosition`, as used by the History panel. */
export function jumpHistory(targetPosition: number): void {
  const doc = activeDocument();
  if (!doc) return;
  const resources = engine.peek(doc.id);
  if (!resources) return;
  let guard = 0;
  while (resources.history.position > targetPosition && guard < 200) {
    undo();
    guard += 1;
  }
  while (resources.history.position < targetPosition && guard < 200) {
    redo();
    guard += 1;
  }
}

// --- View ---

export function setZoom(zoom: number, anchor?: Point): void {
  const doc = activeDocument();
  if (!doc) return;
  const current = state().views[doc.id];
  if (!current) return;
  const canvas = engine.canvasSize;
  const next = anchor
    ? zoomAt(current, canvas, anchor, zoom)
    : { ...current, zoom: clamp(zoom, MIN_ZOOM, MAX_ZOOM) };
  state().setView(doc.id, next);
  engine.invalidate();
}

export function zoomStep(direction: 1 | -1, anchor?: Point): void {
  const doc = activeDocument();
  if (!doc) return;
  const current = state().views[doc.id];
  if (!current) return;
  setZoom(nextZoomStep(current.zoom, direction), anchor);
}

export function fitToScreen(): void {
  const doc = activeDocument();
  if (!doc) return;
  state().setView(doc.id, {
    zoom: fitZoom(engine.canvasSize, doc.width, doc.height),
    centerX: doc.width / 2,
    centerY: doc.height / 2,
    rotation: 0,
  });
  engine.invalidate();
}

/** View - Rotate View. The angle is a view property, so no pixels are touched. */
export function setViewRotation(degrees: number): void {
  const doc = activeDocument();
  if (!doc) return;
  const normalised = ((degrees % 360) + 360) % 360;
  state().patchView(doc.id, { rotation: normalised });
  engine.invalidate();
}

export function rotateViewBy(degrees: number): void {
  const doc = activeDocument();
  const current = doc ? state().views[doc.id] : null;
  if (!current) return;
  setViewRotation(current.rotation + degrees);
}

export function actualPixels(): void {
  const doc = activeDocument();
  if (!doc) return;
  state().patchView(doc.id, { zoom: 1 });
  engine.invalidate();
}

export function panBy(dx: number, dy: number): void {
  const doc = activeDocument();
  if (!doc) return;
  const current = state().views[doc.id];
  if (!current) return;
  state().setView(doc.id, {
    ...current,
    centerX: current.centerX - dx / current.zoom,
    centerY: current.centerY - dy / current.zoom,
  });
  engine.invalidate();
}

// --- Colours ---

export function pickColor(point: Point, fromComposite: boolean): void {
  const doc = activeDocument();
  if (!doc) return;
  const x = Math.floor(point.x);
  const y = Math.floor(point.y);
  if (x < 0 || y < 0 || x >= doc.width || y >= doc.height) return;

  const resources = engine.resourcesFor(doc);
  const rect: Rect = { x, y, width: 1, height: 1 };
  const pixels = fromComposite
    ? engine
        .compositeToTexture(doc, { view: state().views[doc.id], showSelection: false })
        .readPixels(rect)
    : (resources.layer(doc.activeLayerId ?? '')?.surface.readPixels(rect) ?? null);
  if (!pixels) return;

  state().setForeground({
    r: pixels[0] / 255,
    g: pixels[1] / 255,
    b: pixels[2] / 255,
    a: 1,
  });
}

// --- Export ---

export async function exportActiveDocument(settings: ExportSettings): Promise<void> {
  const doc = activeDocument();
  if (!doc) return;
  state().setStatus('Encoding image…');
  try {
    const blob = await exportDocument(engine, doc, settings);
    const safeName = doc.name.replace(/[\\/:*?"<>|]+/g, '_') || 'pixotah';
    downloadBlob(blob, `${safeName}.${EXPORT_EXTENSIONS[settings.format]}`);
    state().setStatus(`Saved ${safeName}.${EXPORT_EXTENSIONS[settings.format]}`);
    state().replaceDocument(doc.id, (current) => ({ ...current, dirty: false }));
  } catch (error) {
    state().setStatus(error instanceof Error ? error.message : 'Export failed');
  }
}

export function defaultForegroundOrBlack(): RGBA {
  return state().foreground ?? BLACK;
}
